<script setup lang="ts">
import { ref, computed } from 'vue';
import { Field, useForm } from 'vee-validate';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import EditContactName from '@/consent/medicare/infrastructure/components/EditContactName.vue';
import { useMedicareConsentStore } from '@/consent/medicare/infrastructure/composition/useMedicareConsentStore';

const { medicareConsentStore, medicareConsentState } = useMedicareConsentStore();

const { handleSubmit, validate } = useForm();

const runValidations = handleSubmit(() => true);
const firstNameSignature = ref(medicareConsentState.value.application.signature.first);
const lastNameSignature = ref(medicareConsentState.value.application.signature.last);

const showEditNameModal = ref(false);

const sectionTitle = computed(() => medicareConsentState.value.application.appointment_required ? 'Schedule an Appointment' : 'Disclosure Signature');

const sectionSubmit = async (): Promise<boolean | undefined> => {
  const localValid = await runValidations();

  if (!localValid) {
    return false;
  }

  medicareConsentStore.updateForm({
    signature: {
      first: firstNameSignature.value,
      last: lastNameSignature.value,
    },
  });

  return true;
};

defineExpose({
  sectionSubmit: sectionSubmit,
  sectionIsValid: async (): Promise<boolean> => {
    const result = await validate({ mode: 'silent' });

    return result.valid;
  }
});
</script>

<template>
  <BaseSection :title="sectionTitle">
    <div class="text-blue-900 text-lg">
      <p class="mb-5"><strong>By signing this form, you agree to a meeting with a sales agent to discuss the types of products initialed in the previous step.</strong> Please note, the person who will discuss the products is either employed or contracted by a medicare plan. They do not work directly for the Federal government. This individual may also be paid based on your enrollment, or enroll you in a Medicare plan.</p>
      <div class="border">
        <p class="bg-gray-100 px-5 py-3">Electronic Signature</p>
        <div class="flex flex-row items-center space-x-3 px-5 py-3 mb-3">
          <p class="">Enter first and last name: <strong>{{ medicareConsentState.application.contactInfo.firstName }} {{ medicareConsentState.application.contactInfo.lastName }}</strong></p>
          <button class="cursor-pointer" @click="showEditNameModal = true">
            <hfc-icon icon="pencil" class="text-blue-500 h-4" />
          </button>
        </div>
        <div>
          <div class="form-inline mb-2 mt-5 px-5 gap-5">
            <div>
              <Field v-model="firstNameSignature" type="text" v-slot="{ field, errorMessage, meta }" name="electronicSignatureFirst" :rules="{required: true, min: 2, max: 100, regex: '^[a-zA-Z\\s_-]*$', confirmed: medicareConsentState.application.contactInfo.firstName}">
                <input placeholder="First Name" v-autofocus="true" v-capitalized="field" v-only-letters="'.-'" v-bind="field" :class="{ ...meta }" class="">
                <span class="error">{{ errorMessage }}</span>
              </Field>
            </div>
            <div>
              <Field v-model="lastNameSignature" type="text" v-slot="{ field, errorMessage, meta }" name="electronicSignatureLast" :rules="{required: true, min: 2, max: 100, regex: '^[a-zA-Z\\s_-]*$', confirmed: medicareConsentState.application.contactInfo.lastName}">
                <input placeholder="Last Name" v-capitalized="field" v-only-letters="'.-'" v-bind="field" :class="{ ...meta }" class="">
                <span class="error">{{ errorMessage }}</span>
              </Field>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseSection>
  <EditContactName v-if="showEditNameModal" @close="showEditNameModal = false" />
</template>
